import { ReactNode } from 'react';

import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

import { colors } from './colors';

// Define custom breakpoints
export const breakpoints = {
  values: {
    mobile: 0,
    tablet: 640,
    laptop: 1024,
    desktop: 1200,
    desktopWide: 1500,
  },
};

// Create the theme with custom breakpoints and other configurations
const theme = createTheme({
  breakpoints: breakpoints,
  palette: {
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
    },
    background: {
      default: colors.primary.text,
    },
    text: {
      primary: colors.primary.dark,
      secondary: colors.input.text.secondary,
    },
    error: {
      main: colors.creativity.spicy,
    },
  },
  typography: {
    fontFamily: 'LabGrotesque, Arial, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 36,
      fontWeight: 700,
      color: colors.primary.dark,
    },
    h2: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 16,
      fontWeight: 700,
      lineHeight: '26px',
      color: colors.primary.dark,
    },
    h3: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontWeight: 500,
      color: colors.primary.dark,
    },
    h6: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 14,
      fontWeight: 700,
      color: colors.primary.dark,
    },
    body1: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20px',
      color: colors.primary.dark,
    },
    body2: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 14,
      fontWeight: 400,
      color: colors.primary.dark,
      lineHeight: '20px',
    },
    caption: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      fontSize: 12,
      fontWeight: 700,
      color: colors.primary.dark,
    },
    button: {
      fontFamily: 'LabGrotesque, Arial, sans-serif',
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 700,
      color: colors.primary.dark,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          font-family: 'LabGrotesque', Arial, sans-serif; /* fix the very basic font assignment */
          color: ${colors.text.primary}
        }
        .text-medium {
          font-weight: 500 !important;
        }
        .customDescription {
          color: ${colors.text.secondary};
          background: #F8F8FA;
          padding: 8px 16px;
          border-radius: 6px;
        },
        .customAlertMessage {
          background: #FFF9C4;
          padding: 8px 16px;
          border-radius: 6px;
        }
      `,
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: colors.secondary.light,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: colors.secondary.light,
          '.MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.primary.light}`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.input.border.hover}`, // Hover border color
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.input.border.active}`, // Focused border color
          },
          '& .MuiOutlinedInput-input': {
            '&::placeholder': {
              color: colors.input.text.hint,
              opacity: 1,
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: 0,
          height: 40,
          color: colors.primary.dark,
          '&.MuiButton-contained': {
            backgroundColor: colors.primary.main,
            color: '#fff',
            border: 'none',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
            '&:active': {
              backgroundColor: colors.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: colors.primary.light,
              color: colors.secondary.text,
            },
          },
          '&.Mui-disabled': {
            background: colors.primary.light,
            color: colors.secondary.text,
          },
          '&:hover': {
            backgroundColor: colors.primary.light,
            border: 0,
          },
          '&:active': {
            backgroundColor: colors.custom.bgPressed,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.primary.light,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: ({ theme }) => ({
          // These styles will ONLY apply on screens smaller than 640px
          [theme.breakpoints.down('tablet')]: {
            justifyContent: 'flex-start',
            overflow: 'auto',
          },
        }),
        paper: {
          minWidth: 640,
          margin: '8px',
        },
      },
    },
  },
});

export function ApplyTheme({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
